/**
 * The quiz's voice: a blip per answer that climbs as you get further in, a thud
 * for the milestones, four notes at the end. Synthesised rather than loaded —
 * nothing to download, and nothing to host.
 *
 * Every call sits behind a tap, so the AudioContext is only ever created once a
 * player has chosen to play. A storefront must not make a sound at someone who
 * is scrolling past it.
 */
const MUTED_KEY = 'quiz-muted';

let ctx: AudioContext | null = null;
let muted: boolean | null = null;

export function isQuizMuted(): boolean {
  if (muted === null) {
    try {
      muted = localStorage.getItem(MUTED_KEY) === '1';
    } catch {
      muted = false;
    }
  }
  return muted;
}

export function setQuizMuted(next: boolean): void {
  muted = next;
  try {
    localStorage.setItem(MUTED_KEY, next ? '1' : '0');
  } catch {
    // private mode: the choice holds for this session only
  }
}

/**
 * Never throws. Sound rides inside answer handlers, and an AudioContext that
 * refuses to construct (Safari quirks, per-page context limits, hardened
 * privacy modes) must not take the quiz down with it — a silent quiz is fine,
 * an unplayable one is not.
 */
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined' || isQuizMuted()) return null;
  try {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function gain(ac: AudioContext, value: number, at: number, decay: number): GainNode {
  const g = ac.createGain();
  g.gain.setValueAtTime(value, at);
  g.gain.exponentialRampToValueAtTime(0.001, at + decay);
  return g;
}

export function playAnswerBlip(comboCount: number) {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;

  const baseFreq = 520 + Math.min(comboCount * 18, 400);

  const osc = ac.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(baseFreq, now);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.4, now + 0.04);

  const g = gain(ac, 0.18, now, 0.12);
  osc.connect(g);
  g.connect(ac.destination);

  osc.start(now);
  osc.stop(now + 0.12);
}

export function playMilestoneSlam() {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;

  const thud = ac.createOscillator();
  thud.type = 'sine';
  thud.frequency.setValueAtTime(180, now);
  thud.frequency.exponentialRampToValueAtTime(40, now + 0.18);
  const thudGain = gain(ac, 0.45, now, 0.22);
  thud.connect(thudGain);
  thudGain.connect(ac.destination);
  thud.start(now);
  thud.stop(now + 0.22);

  const sweep = ac.createOscillator();
  sweep.type = 'sawtooth';
  sweep.frequency.setValueAtTime(300, now + 0.05);
  sweep.frequency.exponentialRampToValueAtTime(1400, now + 0.28);
  const sweepGain = gain(ac, 0.12, now + 0.05, 0.3);
  sweep.connect(sweepGain);
  sweepGain.connect(ac.destination);
  sweep.start(now + 0.05);
  sweep.stop(now + 0.35);
}

export function playQuizComplete() {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;

  const notes = [523, 659, 784, 1047]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const t = now + i * 0.1;
    const osc = ac.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t);
    const g = gain(ac, 0.22, t, 0.35);
    osc.connect(g);
    g.connect(ac.destination);
    osc.start(t);
    osc.stop(t + 0.35);
  });
}
